/**
 * useLineDrag Hook
 *
 * Thin wrapper for line-specific whole-geometry drag functionality.
 * Delegates to generic `useGeometryWholeDrag` hook with line configuration.
 *
 * Features:
 * - Drag entire line geometry (not individual vertices)
 * - Works with MapLibre GL JS feature queries
 * - Security: Only works when drag mode active + correct task
 * - Prevents conflicts with point handles (vertex drag)
 * - Automatic history integration via Zustand
 * - Auto-pan support when dragging near edges
 *
 * @module hooks/useLineDrag
 */

import { useMemo } from 'react';
import { useGeometryWholeDrag } from './drag/useGeometryWholeDrag';
import { createLineDragConfig } from './drag/configs/lineDragConfig';

/**
 * Custom hook for line drag functionality
 *
 * Supports whole-line dragging using MapLibre queryRenderedFeatures.
 *
 * Drag is enabled when ALL conditions are met:
 * 1. Drawing mode is active (isDrawing = true)
 * 2. Task form is open (create/edit)
 * 3. This geometry belongs to the currently selected task (or is preview)
 * 4. Active tool is not Selection mode
 *
 * Automatically skips drag when clicking on point handles to avoid conflicts.
 *
 * @param geometryId - The ID of the line geometry being dragged
 * @param taskId - The ID of the task this geometry belongs to
 * @returns Object containing drag state
 */
export const useLineDrag = (geometryId: string, taskId: string) => {
  // Create line-specific configuration (memoized to prevent unnecessary re-renders)
  const config = useMemo(() => createLineDragConfig(), []);

  // Delegate to generic drag hook with line config
  return useGeometryWholeDrag(geometryId, taskId, config);
};
